import React, { useMemo } from 'react';
import { Checkbox, Tooltip } from 'antd';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { EditOutlined, HourglassOutlined, PlusOutlined } from '@ant-design/icons';
import {
  HEAVY_METRICS_MESSAGE,
  TEMPLATE_METRIC_PROCESSING_MESSAGE,
  USER_DEFINED_METRIC_CREATING_MESSAGE,
  isAppModule,
  PRODUCT_MODE_MAP,
} from '@utils/constants';
import { kpiDropdownFeatureSelector } from '@store/common/selectors';
import {
  CheckBoxList,
  StyledCheckBox,
  MetricDivider,
  StyledText,
  CheckboxWrapper,
  CategoryWrapper,
  TemplateCheckboxWrapper,
  TemplateStyledCheckBox,
} from './styled';
import KPIDropdown from './KPIDropdown';
import { contextTypeAtom } from '@src/atoms/page';

const CheckboxGroup = ({
  categoryKey,
  listCategory,
  inputList = [],
  onChange,
  onBulkChange,
  setRef,
  tempKpi,
  setTempKpi,
  groupChecked = {},
  hasSearchText,
  emptyText,
  onActivate = (c) => {
    return c;
  },
  isTemplate = false,
  isUserDefined = false,
  contentType = 'metric',
}) => {
  const contextType = useAtomValue(contextTypeAtom);
  const currentListCategory = listCategory[contextType];
  const category = currentListCategory[categoryKey];
  const listItemKeys = inputList.map((item: any) => item.key);
  const KPIDropdownEnabled = useSelector(kpiDropdownFeatureSelector);
  const isMetric = contentType === 'metric';
  const isAppContext = isAppModule || contextType === PRODUCT_MODE_MAP.APP;

  const handleClick = (event) => {
    const checked = event.target.checked;
    if (isAppContext && (isTemplate || isUserDefined) && checked && isMetric) {
      // only defined template metrics can be selected
      const res = inputList.filter((item: any) => !item.disabled).map((item: any) => item.key);
      onBulkChange(checked, res);
      return;
    }
    onBulkChange(checked, listItemKeys);
  };

  // only one group can be bulk selected at a time
  const disableGroup = useMemo(() => {
    return Object.values(groupChecked).filter((g) => g).length > 0 && !groupChecked[categoryKey];
  }, [groupChecked]);

  const emptyList = inputList.length === 0;
  const showKPIDropdown = isAppContext ? true : KPIDropdownEnabled;

  const handleClickActivate = () => {
    onActivate?.(categoryKey);
  };

  const getCheckboxGroup = () => {
    if (emptyList) {
      return emptyText ?? <>No data</>;
    }
    if (isAppContext && (isTemplate || isUserDefined)) {
      const tooltipText = isMetric
        ? isTemplate
          ? TEMPLATE_METRIC_PROCESSING_MESSAGE
          : USER_DEFINED_METRIC_CREATING_MESSAGE
        : '';
      return (
        <TemplateCheckboxWrapper>
          {inputList.map((c: any) => {
            return c.definitionStatus === 'in_progress' ? (
              <TemplateStyledCheckBox key={c.key}>
                <Tooltip title={tooltipText} placement="topLeft">
                  <Checkbox
                    onChange={onChange}
                    checked={c.checked}
                    disabled={c.disabled}
                    value={c.key}
                    key={c.key}
                  >
                    <div className="name-wrapper">
                      {c.name}
                      <span style={{ fontStyle: 'italic', marginLeft: '4px' }}>
                        {'(processing...)'}
                      </span>
                    </div>
                  </Checkbox>
                </Tooltip>
              </TemplateStyledCheckBox>
            ) : (
              <TemplateStyledCheckBox key={c.key}>
                <Checkbox
                  onChange={onChange}
                  checked={c.checked}
                  disabled={c.disabled}
                  value={c.key}
                  key={c.key}
                >
                  <div
                    className="name-wrapper"
                    style={{
                      color:
                        c.definitionStatus === 'undefined' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.88)',
                    }}
                  >
                    {c.name}
                  </div>
                </Checkbox>
              </TemplateStyledCheckBox>
            );
          })}
        </TemplateCheckboxWrapper>
      );
    }
    return (
      <CheckboxWrapper count={inputList.length}>
        {inputList.map((c: any) => (
          <StyledCheckBox key={c.key}>
            <Checkbox
              onChange={onChange}
              checked={c.checked}
              disabled={c.disabled}
              value={c.key}
              key={c.key}
            >
              {c.name}
              {c.isHeavyMetric && c.checked ? (
                <Tooltip title={HEAVY_METRICS_MESSAGE}>
                  <HourglassOutlined />
                </Tooltip>
              ) : null}
            </Checkbox>
          </StyledCheckBox>
        ))}
      </CheckboxWrapper>
    );
  };

  return (
    <CheckBoxList key={categoryKey} ref={setRef}>
      <CategoryWrapper>
        <StyledText>
          {!isEmpty(groupChecked) ? (
            <Checkbox
              onChange={handleClick}
              key={categoryKey}
              checked={groupChecked[categoryKey]}
              disabled={emptyList || disableGroup || hasSearchText}
              style={{ fontSize: 16 }}
            >
              {category}
            </Checkbox>
          ) : (
            category
          )}
        </StyledText>
        {isTemplate && <EditOutlined className="activate-button" onClick={handleClickActivate} />}
      </CategoryWrapper>
      <MetricDivider />
      {[currentListCategory.SP, currentListCategory.QOE].includes(category) &&
        showKPIDropdown &&
        !!tempKpi &&
        !!setTempKpi && <KPIDropdown tempKpi={tempKpi} setTempKpi={setTempKpi} />}
      {getCheckboxGroup()}
    </CheckBoxList>
  );
};

export default CheckboxGroup;
